import { type ReactElement } from 'react'
import { useRequiredParams } from '@util'
import { isTeamId, type WorkflowId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useCloseNavbar } from '@hooks'
import { usePermissionCheck, useProfile, useTranslation, useWorkflow, useWorkflowFeature } from '@front/model'
import { NotReady } from '@components'
import { EditWorkflowForm } from './EditWorkflowForm'
import { EditWorkflowProvider } from './EditWorkflowContext'

function EditWorkflowPage(): ReactElement {
  const { t } = useTranslation()
  const { isEnabled: isWorkflowEnabled, isLoading: isLoadingFeature } = useWorkflowFeature()
  const { workflowId } = useRequiredParams<{ workflowId: WorkflowId }>()
  const { user } = useProfile()

  const { data: workflow, isLoading, error, refetch, isFetching } = useWorkflow(workflowId)

  const { data: canManageWorkflow, isLoading: isLoadingPermission } = usePermissionCheck({
    permissionType: 'workflow:manage',
    targetId: [user?.companyId, ...(workflow?.visibleTo ?? []).filter(isTeamId)],
  })

  useCloseNavbar()

  if (!workflow || error || isLoading || isFetching || isLoadingPermission)
    return <NotReady data={workflow} error={error} id='Workflow' isLoading={isFetching} />

  if (!isWorkflowEnabled || isLoadingFeature)
    return <NotReady isLoading={isLoadingFeature} error={t('Workflows are not available')} />

  return (
    <EditWorkflowProvider workflow={workflow} isFetching={isFetching} refetch={refetch} readOnly={!canManageWorkflow}>
      <EditWorkflowForm />
    </EditWorkflowProvider>
  )
}

export const Route = createFileRoute('/settings/workflow_/$workflowId/')({
  component: EditWorkflowPage,
})
